import { formatBRL, type QuoteStatus } from '@dnx/contracts';
import { Badge, Button, Card, Dialog, EmptyState, PageHeader, Spinner, cn } from '@dnx/ui';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { FileText, Plus, ShieldAlert } from 'lucide-react';
import { useState } from 'react';
import { meQuery } from '../lib/session.js';
import { brDate, QUOTE_STATUS, quotableQuery, quotesQuery } from './queries.js';

const FILTERS: { value: 'all' | QuoteStatus; label: string }[] = [
  { value: 'all', label: 'Todos' }, { value: 'draft', label: 'Rascunhos' }, { value: 'sent', label: 'Enviados' },
  { value: 'accepted', label: 'Aceitos' }, { value: 'expired', label: 'Vencidos' },
];

/** Escolhe o paciente (avaliação assinada) e abre o editor. */
export function NewQuoteDialog({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const { data, isLoading } = useQuery(quotableQuery);
  return (
    <Dialog open onClose={onClose} title="Novo orçamento" description="Só aparecem pacientes com avaliação capilar assinada pelo médico.">
      <div className="p-2">
        {isLoading ? <Spinner className="m-4 size-5 text-fg-subtle" label="Carregando" /> : data?.length ? (
          <ul className="grid">
            {data.map((e) => (
              <li key={e.evaluationId}>
                <button type="button" onClick={() => void navigate({ to: '/orcamentos/novo', search: { avaliacao: e.evaluationId } })}
                  className="grid w-full gap-0.5 rounded-lg px-3 py-2.5 text-left hover:bg-surface-muted">
                  <span className="flex items-center gap-2 font-medium text-fg">{e.patientName}
                    {e.openQuotes > 0 && <Badge tone="primary">{e.openQuotes} em aberto</Badge>}</span>
                  <span className="text-[12.5px] text-fg-muted">
                    Avaliação de {e.doctorName} em {new Date(e.signedAt).toLocaleDateString('pt-BR')}
                    {e.graftsEstimateMin != null && ` · ${e.graftsEstimateMin}${e.graftsEstimateMax ? `–${e.graftsEstimateMax}` : ''} UFs`}
                    {e.technique && ` · ${e.technique}`}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        ) : <p className="p-4 text-sm text-fg-muted">Nenhum paciente com avaliação assinada ainda. O médico assina a avaliação na ficha do paciente.</p>}
      </div>
    </Dialog>
  );
}

export function QuotesPage() {
  const { data: me } = useSuspenseQuery(meQuery);
  const canWrite = me.permissions.includes('billing.quote.write');
  const { data, isLoading } = useQuery(quotesQuery());
  const [filter, setFilter] = useState<'all' | QuoteStatus>('all');
  const [creating, setCreating] = useState(false);
  const rows = (data ?? []).filter((q) => filter === 'all' || q.status === filter);
  const pending = (data ?? []).filter((q) => q.discountPending).length;
  const open = (data ?? []).filter((q) => q.status === 'sent');
  return (
    <div className="mx-auto max-w-6xl">
      <PageHeader title="Orçamentos" description="Montados a partir da avaliação assinada, com a tabela de preços da clínica. Cada alteração vira uma nova versão."
        actions={canWrite && <Button onClick={() => setCreating(true)}><Plus className="size-4" />Novo orçamento</Button>} />
      {data && data.length > 0 && (
        <div className="mt-7 grid gap-3 sm:grid-cols-3">
          <Card className="p-4"><p className="text-[12px] font-medium tracking-wide text-fg-subtle uppercase">Enviados em aberto</p><p className="mt-1 text-xl font-semibold text-fg tabular-nums">{formatBRL(open.reduce((t, q) => t + q.totalCents, 0))}</p><p className="text-[12.5px] text-fg-muted">{open.length} orçamento(s)</p></Card>
          <Card className="p-4"><p className="text-[12px] font-medium tracking-wide text-fg-subtle uppercase">Aceitos</p><p className="mt-1 text-xl font-semibold text-fg tabular-nums">{formatBRL(data.filter((q) => q.status === 'accepted').reduce((t, q) => t + q.totalCents, 0))}</p><p className="text-[12.5px] text-fg-muted">{data.filter((q) => q.status === 'accepted').length} orçamento(s)</p></Card>
          <Card className={cn('p-4', pending && 'border-warning/40')}><p className="text-[12px] font-medium tracking-wide text-fg-subtle uppercase">Desconto aguardando</p><p className="mt-1 text-xl font-semibold text-fg tabular-nums">{pending}</p><p className="text-[12.5px] text-fg-muted">aprovação da gestão</p></Card>
        </div>
      )}
      <div className="mt-6 flex flex-wrap gap-1.5">
        {FILTERS.map((f) => (
          <button key={f.value} type="button" aria-pressed={filter === f.value} onClick={() => setFilter(f.value)}
            className={cn('h-8 rounded-full border px-3 text-[13px]', filter === f.value ? 'border-primary bg-primary-soft text-primary' : 'border-border-strong text-fg-muted hover:text-fg')}>
            {f.label}
          </button>
        ))}
      </div>
      {isLoading ? <Spinner className="mt-10 size-5 text-fg-subtle" label="Carregando" /> : rows.length ? (
        <Card className="mt-4 divide-y divide-border overflow-hidden">
          {rows.map((q) => (
            <Link key={q.id} to="/orcamentos/$id" params={{ id: q.id }} className="grid gap-2 px-5 py-3.5 transition-colors hover:bg-surface-muted sm:grid-cols-[110px_minmax(0,1fr)_160px_150px] sm:items-center">
              <span className="font-mono text-[12.5px] text-fg-muted">{q.code}{q.version > 1 && <span className="text-fg-subtle"> · v{q.version}</span>}</span>
              <span className="min-w-0">
                <span className="block truncate font-medium text-fg">{q.patientName}</span>
                <span className="text-[12.5px] text-fg-muted">{q.status === 'accepted' && q.acceptedAt ? `aceito em ${new Date(q.acceptedAt).toLocaleDateString('pt-BR')}` : `válido até ${brDate(q.validUntil)}`}</span>
              </span>
              <span className="flex flex-wrap gap-1.5">
                <Badge tone={QUOTE_STATUS[q.status][1]}>{QUOTE_STATUS[q.status][0]}</Badge>
                {q.discountPending && <Badge tone="warning"><ShieldAlert className="size-3" />Desconto</Badge>}
              </span>
              <span className="font-semibold text-fg tabular-nums sm:text-right">{formatBRL(q.totalCents)}</span>
            </Link>
          ))}
        </Card>
      ) : data?.length ? <p className="mt-6 text-sm text-fg-muted">Nenhum orçamento neste filtro.</p> : (
        <EmptyState className="mt-8" icon={<FileText className="size-6" />} title="Nenhum orçamento ainda"
          actions={canWrite && <Button onClick={() => setCreating(true)}>Montar o primeiro</Button>}>
          <p>Cadastre os itens em Configurações › Preços. Depois que o médico assina a avaliação, o orçamento sai em poucos cliques e vai pelo WhatsApp.</p>
        </EmptyState>
      )}
      {creating && <NewQuoteDialog onClose={() => setCreating(false)} />}
    </div>
  );
}
